import type { UseMutateAsyncFunction } from '@tanstack/react-query'
import type { AxiosError, AxiosResponse } from 'axios'
import { useCallback, useEffect, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { Dropzone } from '@/components/ui/dropzone'
import { ManualRetouchingUploadCard } from '@/features/photoset/components/manual-retouching-upload-card'
import { useBulkUploadPhotos } from '@/features/photoset/queries/use-bulk-upload-photos'
import { useUploadPhoto } from '@/features/photoset/queries/use-upload-photo'
import type { PhotoPresignedData, Photoset } from '@/features/photoset/types'
import { QUERY_KEYS, queryClient } from '@/query-client'
import { useTranslation } from '@/translations/i18n'

export const ManualRetouchingUploader = ({
	photosetId,
	isDisabled,
	onUploadCb
}: {
	photosetId: number
	isDisabled: boolean
	onUploadCb: UseMutateAsyncFunction<
		AxiosResponse,
		AxiosError,
		Photoset['id']
	>
}) => {
	const { t } = useTranslation()
	const [uploadingFiles, setUploadingFiles] = useState<File[] | null>(null)
	const [uploadedImagesCounter, setUploadedImagesCounter] = useState(0)
	const [progress, setProgress] = useState(0)

	const uploadPhotoMutation = useUploadPhoto({
		photosetId,
		onUploadProgress: (progressEvent) => {
			if (!progressEvent.total) {
				return
			}
			setProgress(
				Math.round((progressEvent.loaded * 100) / progressEvent.total)
			)
		},
		options: {
			onSettled: () => setProgress(0),
			onSuccess: () => setUploadedImagesCounter((prev) => prev + 1)
		}
	})

	const bulkUploadPhotosMutation = useBulkUploadPhotos({
		photosetId,
		options: {
			onSuccess: async (data: PhotoPresignedData[]) => {
				for (const [index, presignedItem] of data.entries()) {
					if (uploadingFiles?.[index]) {
						await uploadPhotoMutation.mutateAsync({
							imageId: presignedItem.media.id,
							payload: {
								presignedUrl: presignedItem.presigned_url,
								file: uploadingFiles[index]
							}
						})
					}
				}
			},
			onError: () => {
				setUploadingFiles(null)
				setUploadedImagesCounter(0)
			}
		}
	})

	const dropzoneState = useDropzone({
		disabled: isDisabled,
		accept: {
			'image/jpeg': ['.jpeg'],
			'image/jpg': ['.jpg']
		},
		multiple: true,
		maxFiles: 999,
		onDropAccepted: (acceptedFiles) => {
			setUploadingFiles(acceptedFiles)
			bulkUploadPhotosMutation.mutate({
				medias: acceptedFiles.map((file) => file.name)
			})
		},
		onDropRejected: (rejectedFiles) => {
			console.error('Rejected files: ', rejectedFiles)
		}
	})

	const handleUpdateStep = useCallback(async () => {
		await onUploadCb(photosetId)
		queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PHOTOSET] })
		queryClient.invalidateQueries({
			queryKey: [QUERY_KEYS.MANUAL_RETOUCHING_SPACES]
		})
		queryClient.invalidateQueries({
			queryKey: [QUERY_KEYS.PHOTOSET_SPACES]
		})
	}, [onUploadCb, photosetId])

	useEffect(() => {
		if (uploadingFiles && uploadingFiles.length === uploadedImagesCounter) {
			handleUpdateStep()
		}
	}, [uploadingFiles, uploadedImagesCounter, handleUpdateStep])

	if (uploadingFiles) {
		return (
			<ManualRetouchingUploadCard
				isBulkUploadPhotosPending={bulkUploadPhotosMutation.isPending}
				progress={progress}
				subtitle={t('common:generating_waiting_text')}
				title={t('common:generating_thumbnails')}
				totalUploadingImagesCounter={uploadingFiles?.length || 0}
				uploadedImagesCounter={uploadedImagesCounter}
			/>
		)
	}

	return (
		<Dropzone
			className={`h-full w-full rounded-2xl border-2 border-neutral-300 border-dashed ${
				isDisabled ? 'cursor-not-allowed bg-neutral-100' : ''
			}`}
			state={dropzoneState}
		>
			<div className='flex h-full w-full flex-col items-center justify-center gap-1'>
				<h3 className='font-medium text-neutral-900'>
					{t('common:drop_jpgs_here')}
				</h3>
				<em>{t('common:or')}</em>
				<p className='text-neutral-400 text-sm underline'>
					{t('common:upload_from_your_device')}
				</p>
			</div>
		</Dropzone>
	)
}
